import { useState, useMemo } from 'react'
import { GroupsChannelFormattedRowType } from './useGroupsChannelsFormattedRows'
import { GroupsSiteFormattedRowType } from './useGroupsSitesFormattedRows'

export const useGroupsSortedData = <
    T extends GroupsChannelFormattedRowType | GroupsSiteFormattedRowType,
>(
    data: T[]
) => {
    const [sortConfig, setSortConfig] = useState<{
        key: keyof T
        direction: 'asc' | 'desc'
    } | null>(null)

    const handleSort = (key: keyof T) => {
        let direction: 'asc' | 'desc' = 'asc'
        if (sortConfig?.key === key && sortConfig.direction === 'asc') {
            direction = 'desc'
        }
        setSortConfig({ key, direction })
    }

    const sortedData = useMemo(() => {
        const sorted = [...data]
        if (sortConfig !== null) {
            sorted.sort((a, b) => {
                const { key, direction } = sortConfig
                const aValue = a[key]
                const bValue = b[key]

                if (aValue === null || aValue === undefined) return 1
                if (bValue === null || bValue === undefined) return -1

                if (key === 'creationDate') {
                    const aDate = new Date(aValue as string)
                    const bDate = new Date(bValue as string)
                    return direction === 'asc'
                        ? aDate.getTime() - bDate.getTime()
                        : bDate.getTime() - aDate.getTime()
                }

                if (typeof aValue === 'number' && typeof bValue === 'number') {
                    return direction === 'asc'
                        ? aValue - bValue
                        : bValue - aValue
                }

                if (typeof aValue === 'string' && typeof bValue === 'string') {
                    return direction === 'asc'
                        ? aValue.localeCompare(bValue)
                        : bValue.localeCompare(aValue)
                }

                return 0
            })
        }
        return sorted
    }, [data, sortConfig])

    return { sortedData, handleSort, sortConfig }
}
